// Shared data/behaviour for scheduled WhatsApp organizer messages, mixed
// into BOTH TripCalendarView.vue (the calendar tab) and SingleRide.vue (the
// list view), so there is exactly one implementation of:
//   - the fetch (loadOrganizerMessages / the organizerMessages array)
//   - delete (deleteOrganizerMessage)
//   - the ownership gate (canManageOrganizerMessages)
//   - the day-index -> date derivation (dateForDayIndex)
//
// Each component that mixes this in gets its OWN organizerMessages array
// (component-local reactive data, like any other mixin data()) -- it is not
// a cross-component singleton. Both the calendar and the list view fetch
// independently, the same way EditRideAccessKey.vue holds
// whatsappInvite/reminderSettings locally rather than in Vuex.
//
// Host components are assumed to also use translationMixin (for `this.t`) --
// not re-imported here to avoid double-mixing it in.
import { mapGetters, mapActions } from 'vuex';
import moment from 'moment';
import { partitionOrganizerMessages } from '@/utils/organizerMessageGroups';
import organizerMessagesApi from '@/api/organizerMessages';
import { mapOrganizerMessageError } from '@/utils/organizerMessageErrors';

// Open to every ride manager (owner, 2026-10-18), on PRIVATE rides only
// (ride_manager_id set) that they both own and manage. The server refuses
// public rides (ride_not_private in whatsapp/api._manager_ride_or_none) and
// non-owners (_organizer_owner_or_forbidden); requiring the manager as well
// keeps a traveller who owns a copied ride from seeing it.
//
// A plain function rather than only the computed below, because
// DayManagerOptions.vue needs the same gate for its "schedule a message"
// button and must NOT mix this whole mixin in: the mixin's own watchers
// would fire loadOrganizerMessages, i.e. an extra API round trip every time
// anyone opens the day menu. Exported so there is still exactly one place
// the rule lives.
export function canManageOrganizerMessagesFor( ride, compareToUserId ) {
  return Boolean( ride )
    && typeof ride.ride_manager_id === 'number'
    && compareToUserId( ride.ride_manager_id )
    && compareToUserId( ride.authorId );
}

// One helper, every view: the calendar and the list must never describe the
// same message differently, because the difference between "the whole trip
// gets this" and "only I get this" is the one thing a manager must be able
// to read at a glance.
//
// Plain exported functions, like canManageOrganizerMessagesFor above, because
// OrganizerMessageList.vue renders the marker but must NOT mix this whole
// mixin in (its watchers would fetch the messages a second time).
export function organizerMessageIsPrivateFor( message ) {
  return Boolean( message ) && message.recipient_type === 'ride_manager';
}

export function organizerRecipientLabelFor( message, t ) {
  return organizerMessageIsPrivateFor( message )
    ? t( 'organizerRecipientManagerShort' )
    : t( 'organizerRecipientTravellersShort' );
}

export const organizerMessagesMixin = {
  data() {
    return {
      // Scheduled organizer messages for this ride, fetched once the ride id
      // is known. Kept local to the component (not Vuex) -- mirrors how
      // EditRideAccessKey.vue holds whatsappInvite/reminderSettings locally
      // and calls the API module directly.
      organizerMessages: []
    };
  },
  computed: {
    ...mapGetters([
      'allRide',
      'apptoken',
      'compareToUserId',
      'isRideLocked',
      'userId'
    ]),

    // Ownership gate -- see canManageOrganizerMessagesFor above.
    canManageOrganizerMessages() {
      return canManageOrganizerMessagesFor(this.allRide?.ride, this.compareToUserId);
    },

    // A fingerprint of everything about the ride that can move a message.
    //
    // The two views used to refetch only when allRide.ride.id changed, which
    // never happens while you are editing: inserting a day mid-trip shifts
    // the ride's end_date and every later organizer message's target_date
    // ON THE SERVER (ride/views.updateOrganizerMessageDates), the id stays
    // the same, and the calendar and list went on showing the OLD dates
    // until the page was reloaded by hand.
    //
    // end_date alone would catch a day insert, but not every case: a whole
    // trip moved earlier changes start_date too, and a day deleted changes
    // the day count. Joining all four means any reshape refetches and
    // nothing else does -- a plain allRide refetch with unchanged dates
    // produces the same string and costs no request.
    organizerRideShape() {
      const ride = this.allRide?.ride;
      if (!ride?.id) return '';
      return [ride.id, ride.start_date, ride.end_date,
              (this.allRide?.attractionsByDay || []).length].join('|');
    },

    // The trip's day dates, 'YYYY-MM-DD', in day order.
    organizerTripDates() {
      const dayCount = (this.allRide?.attractionsByDay || []).length;
      const dates = [];
      for (let index = 0; index < dayCount; index += 1) {
        const dateStr = this.dateForDayIndex(index);
        if (dateStr) dates.push(dateStr);
      }
      return dates;
    },

    // Messages whose target_date is not any day of the trip, split by which
    // side of it they fall on.
    //
    // The modal's date field spans today .. end+7 (the owner's window, so
    // "your flight is tomorrow" and "thank you for coming" are schedulable,
    // and since 2026-11-02 any day ahead of the trip too),
    // and it is editable from either view. So a manager can legitimately
    // create a message on a date no trip day represents -- and before this
    // existed, the list view simply dropped it: still scheduled, still
    // billable, findable only on the calendar tab. Worse, the calendar
    // cannot show it either once it falls in an adjacent month: tripMonths
    // renders only the months the trip spans and filterEventsForMonth drops
    // the rest, which the widening from 2 days to 7 made reachable for any
    // trip near a month boundary. Anything that can be created has to be
    // visible somewhere it can be edited or deleted.
    //
    // Split rather than one list so each side can render where it belongs
    // chronologically -- before the trip ABOVE the days, after it below --
    // instead of a pre-trip message appearing after the last day.
    //
    // The partition itself is a tested pure function
    // (utils/organizerMessageGroups.js); its invariant is that no message
    // can fall out of all the buckets, which is the only way one goes
    // invisible while still being sent and billed.
    organizerMessageGroups() {
      return partitionOrganizerMessages(
        this.organizerMessages, this.organizerTripDates);
    },

    organizerMessagesBeforeTrip() {
      return this.organizerMessageGroups.before;
    },

    organizerMessagesAfterTrip() {
      return this.organizerMessageGroups.after;
    }
  },
  watch: {
    // Refetch whenever the ride is reshaped, and on mount once it exists.
    //
    // Replaces the per-view 'allRide.ride.id' watchers, which this subsumes
    // -- the id is part of the fingerprint. Lives in the MIXIN so the
    // calendar and the list cannot drift apart on it; they hold separate
    // message arrays and both were wrong in the same way.
    //
    // Overlapping fetches are safe: loadOrganizerMessages carries a sequence
    // guard so a slower earlier response can never overwrite a newer one.
    organizerRideShape: {
      immediate: true,
      handler(shape) {
        if (shape) {
          this.loadOrganizerMessages();
        }
      }
    },

    // Logging in WITHOUT a page reload is the case this exists for.
    //
    // openLoginForm is a modal, and authenticate only commits the token --
    // no reload, no route change -- so the host view is never destroyed.
    // While logged out, userId is null, the gate is false, and
    // loadOrganizerMessages early-returns. Logging in then calls fetchRide,
    // which commits the SAME ride.id, so the 'allRide.ride.id' watcher does
    // not re-fire, and no tab toggle happens either. The organizer UI would
    // appear with an empty array: every day looking unscheduled, so the
    // manager re-adds a message that is already queued and every traveller
    // receives it twice, billed twice.
    //
    // In the mixin rather than either host, so both views get it.
    canManageOrganizerMessages: {
      handler(canManage) {
        if (canManage) {
          this.loadOrganizerMessages();
        }
      }
    }
  },
  methods: {
    ...mapActions([
      'showModalWithParameters'
    ]),

    // The single derivation of a trip day's date, shared by the calendar's
    // per-day "add" affordance and the list view's per-day row. Mirrors the
    // trip-day arithmetic already used throughout TripCalendarView.vue
    // (handleEventDragStop, handleDateClick, etc) -- start_date is
    // DD-MM-YYYY and local, not the UTC-parsed per-attraction `dateTime`
    // field, so this is kept as its own derivation rather than reused from
    // dayActivites.dateTime (see openOrganizerMessageForDay in
    // SingleRide.vue for why).
    dateForDayIndex(dayIndex) {
      const start = this.allRide?.ride?.start_date;
      if (!start) return null;
      return moment(start, 'DD-MM-YYYY').clone().add(dayIndex, 'days').format('YYYY-MM-DD');
    },

    // The messages scheduled for one trip day, by the same date derivation.
    messagesForDayIndex(dayIndex) {
      const dateStr = this.dateForDayIndex(dayIndex);
      if (!dateStr) return [];
      return this.organizerMessages.filter((message) => message.target_date === dateStr);
    },

    // The sequence number is what stops an OLDER response overwriting a
    // newer one. Two overlapping GETs are easy to cause -- toggle
    // list/calendar/list quickly, or save a message while a toggle's fetch
    // is still in flight -- and if the first resolves last, the list renders
    // the stale snapshot. That is not cosmetic here: a row deleted in
    // between reappears as a ghost that 404s on edit and delete, and a row
    // just saved vanishes, so the manager adds it again and every traveller
    // gets a second BILLED template message. Same failure this view's
    // refetch exists to prevent, reached by response order instead.
    async loadOrganizerMessages() {
      if (!this.canManageOrganizerMessages) return;
      const rideId = this.allRide?.ride?.id;
      if (!rideId) return;
      const sequence = (this._organizerLoadSequence || 0) + 1;
      this._organizerLoadSequence = sequence;
      try {
        const response = await organizerMessagesApi.list(this.apptoken, rideId);
        if (sequence !== this._organizerLoadSequence) return;
        this.organizerMessages = (response.data && response.data.messages) || [];
      } catch (error) {
        // Mirrors loadReminderSettings in EditRideAccessKey.vue: the host
        // view must keep working even if the organizer-messages list cannot
        // be read. Still sequence-checked, so a failed OLD request cannot
        // disturb a list a newer one has already filled.
        //
        // The LAST KNOWN list is kept rather than blanked, the same answer
        // loadAccountLink's catch arrived at: an exception means the answer
        // is UNKNOWN, and `[]` is not "unknown", it is the positive claim
        // "you have no scheduled messages". A manager who believes that
        // writes them again, and every duplicate is a billed template send
        // once it fires.
        if (sequence !== this._organizerLoadSequence) return;
      }
    },

    async deleteOrganizerMessage(message) {
      if (this.isRideLocked) {
        this.showModalWithParameters({
          errorNumber: 'uesr_is_not_authorize_to_edit_this_ride',
          type: 'textConverter'
        });
        return;
      }
      if (!window.confirm(this.t('organizerMessageDeleteConfirm'))) return;
      try {
        await organizerMessagesApi.withdraw(this.apptoken, message.id);
        this.organizerMessages = this.organizerMessages.filter((m) => m.id !== message.id);
      } catch (error) {
        this.$bvToast.toast(
          mapOrganizerMessageError(error, this.t, 'organizerMessageDeleteError'),
          { variant: 'danger', noCloseButton: true });
      }
    },

    organizerRecipientLabel( message ) {
      return organizerRecipientLabelFor( message, this.t );
    },

    organizerMessageIsPrivate( message ) {
      return organizerMessageIsPrivateFor( message );
    },

    // Shared handler for OrganizerMessageModal's `saved` event: the modal
    // hands back the server's row for BOTH add and edit, so this upserts by
    // id rather than needing to know which mode produced it.
    upsertOrganizerMessage(message) {
      const idx = this.organizerMessages.findIndex((m) => m.id === message.id);
      if (idx !== -1) {
        this.organizerMessages.splice(idx, 1, message);
      } else {
        this.organizerMessages.push(message);
      }
    }
  }
};
